import React from "react";
import { Button, Chip, MenuItem, Select, SelectChangeEvent, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { cacheService, shareService } from "../../../Core/Api/services";
import { DbShareMediaInfos } from "../../../Core/Api/Types/db-share-media-infos";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../Components/Table/table";
import TableCellMovie from "../../../Components/Table/TableCellMovie/table-cell-movie";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import { ModalContext } from "../../../Components/Modals";
import ModalShareUpdate, {
  ModalShareUpdateElementProps,
} from "../../../Components/Modals/ModalShareUpdate/modal-share-update";
import TableCellExpiresIn from "../../../Components/Table/TableCellExpiresIn/table-cell-expires-in";
import TableCellShareClips from "../../../Components/Table/TableCellShareClips/table-cell-share-clips";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import ModalCreateShare from "../../../Components/Modals/ModalCreateShare/modal-create-share";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import ShareDetailsPanel from "../../../Components/Panels/ShareDetailsPanel/share-panel";
import TableCellBool from "../../../Components/Table/TableCellBool";
import { ShareMediaType } from "../../../Core/Api/Types/share-media-type";
import TableCellShareStatus from "../../../Components/Table/TableCellShareStatus/table-cell-share-status";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { routePaths } from "../../../Router/router";
import { debounce } from "throttle-debounce";
import { OpenLinkInNewTab, UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import TableCellOwner2Lines from "../../../Components/Table/TableCellOwner2Lines/table-cell-owner-2-lines";
import moment from "moment";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useOutletContext } from "react-router";
import ModalShareMetrics, {
  ModalShareMetricsElementProps,
} from "../../../Components/Modals/ModalShareMetrics/modal-share-metrics";
import { produce } from "immer";
import SecurityIcon from "@mui/icons-material/Security";
import { Licences } from "../../../Core/Api/Types/licences";
import TableCellActionReportIssue from "../../../Components/Table/TableCellActionReportIssue/table-cell-action-report-issue";
import AssetTypeChip from "../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import { TableCellLongTypography } from "../../../Components/Table/TableCellTypography/table-cell-typography";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../Components/Table/table-uuids";

function SharesList() {
  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  // data
  const [shares, setShares] = React.useState<DbShareMediaInfos[]>([]);
  // search
  const [filterText, setFilterText] = React.useState<string>("");
  // types filter
  const [filterType, setFilterType] = React.useState<ShareMediaType | undefined>(undefined);
  // object filter
  const [objects, setObjects] = React.useState<string[]>([ObjectLayer.Content, ObjectLayer.Asset]);
  const [filterObject, setFilterObject] = React.useState<string>("");
  // status filter
  const [statuses, setStatuses] = React.useState<string[]>(Object.values(["Active", "Expired", "Transcoding"]));
  const [filterStatus, setFilterStatus] = React.useState<string>("");
  const [filterViewed, setFilterViewed] = React.useState<"" | "yes" | "never">("");
  const [filterClips, setFilterClips] = React.useState<"" | "with" | "without">("");
  const [filterNex, setFilterNex] = React.useState<"" | "with" | "without">("");

  const [isLoading, setIsLoading] = React.useState(true);
  const [selectedShare, setSelectedShare] = React.useState<DbShareMediaInfos>();
  const { active: shareDetailsPanelOpen, toggle: toggleShareDetailsPanelOpen } = useToggle(false);

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedShares, setSelectedShares } = useOutletContext<{
    setSelectedShares: React.Dispatch<React.SetStateAction<DbShareMediaInfos[]>>;
    selectedShares: DbShareMediaInfos[];
  }>();

  const customSnackbar = useCustomSnackbar();
  const apiGrid = useGridApiRef();

  const updateClipsImportedStatus = (shareId, ClipIds: number[], linkedAssetId: number) => {
    let rowIndex = shares.findIndex((x) => x.id == shareId);
    let row = shares[rowIndex];
    row?.clips?.forEach((x) => {
      if (ClipIds.includes(x.id)) x.importedOnAssetId = linkedAssetId;
    });

    setShares(shares.map((x, index) => (index === rowIndex ? row : x)));
  };

  // Main load
  React.useEffect(() => {
    shareService.GetAllShares(0, 1000).then((response) => {
      if (response.shares) {
        setShares(response.shares);
        setIsLoading(false);
      }
    });
  }, []);

  // Table creation
  const rows: GridRowsProp = shares;
  type Row = (typeof rows)[number];
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("common:object"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: Row) => (
        <TableCellMovie
          id={data.row?.titleInfos?.id ?? data?.row?.objectId}
          name={data.value}
          customerId={data.row?.titleInfos?.titleCustomerId ?? "Drive"}
          idDisplayed="customerId"
          isDrive={!data.row?.titleInfos}
        />
      ),
    },
    {
      field: "objectLayer",
      headerName: t("common:object"),
      type: "singleSelect",
      valueOptions: (data: Row) => Object.keys(ObjectLayer),
      hideable: true,
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: Row) => (
        <AssetTypeChip size="small" type={data.value} />
        //<Chip label={data.value} key={data.value} size="small" color={"default"} sx={{ mr: 0.5 }} />
      ),
    },
    {
      field: "comment",
      headerName: t("common:comment"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <TableCellLongTypography data={data.value} variant="body1" sx={{ color: "text.secondary" }} />
      ),
    },
    {
      field: "watermarkText",
      headerName: t("common:watermark"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => (
        <Typography variant="body1" sx={{ color: "text.secondary", wordBreak: "break-word" }}>
          {data?.row?.watermarkSecurity !== null && (
            <Tooltip title={t("tooltip:title.nexguardEnabled")}>
              <SecurityIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
            </Tooltip>
          )}
          {data.value}
        </Typography>
      ),
    },
    {
      field: "isMultiple",
      headerName: t("common:multiple"),
      type: "boolean",
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "sharedBy",
      headerName: t("table:headerName.sharedBy"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      valueGetter: (data: any, row: any) => row?.creationInfos.date + " - " + row?.createdByUserName,
      renderCell: (data: Row) => (
        <TableCellOwner2Lines
          userName={data.row["createdByUserName"]}
          groupName={moment(data?.row?.creationInfos.date).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
        />
      ),
    },
    {
      field: "daysUntilExpire",
      headerName: t("table:headerName.expiresInUpperCase"),
      renderCell: (data: Row) => (data.value >= 0 ? <TableCellExpiresIn daysNumber={data.value} /> : ""),
      flex: CellSize.small,
      minWidth: CellSize.small,
    },
    /*{
          field: "ipsUntilExpire",
          headerName: "IP Expires",
          renderCell: (data: Row) => data.value,
        },*/
    {
      field: "isViewed",
      type: "boolean",
      headerName: t("common:viewed"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "isActive",
      headerName: t("common:status"),
      type: "singleSelect",
      valueOptions: ["Expired", "Active", "Transcoding"],
      valueGetter: (data: Row, row: any) => {
        if (!row) return;
        let transcoding =
          row?.linkedOrderItemStatus === OrderStatus.READY ||
          row?.linkedOrderItemStatus === OrderStatus.QUEUED ||
          row?.linkedOrderItemStatus === OrderStatus.RUNNING;

        if (!transcoding) {
          switch (row?.isActive) {
            case false:
              return "Expired";
            default:
              return "Active";
          }
        } else {
          return "Transcoding";
        }
      },
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => {
        let transcoding =
          data?.row?.linkedOrderItemStatus === OrderStatus.READY ||
          data?.row?.linkedOrderItemStatus === OrderStatus.QUEUED ||
          data?.row?.linkedOrderItemStatus === OrderStatus.RUNNING;

        return (
          <TableCellShareStatus
            data={{
              value: data?.row?.isActive,
              nbViews: data?.row?.viewInfos?.length ?? 0,
              nbTotalViews: data?.row?.viewsMax,
              transcoding: transcoding,
            }}
          />
        );
      },
    },
    {
      field: "clips",
      headerName: t("common:clips"),
      type: "number",
      align: "left",
      headerAlign: "left",
      valueGetter: (data: any, row: any) => row?.clips?.length || 0,
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => (
        <TableCellShareClips
          clips={data?.row?.clips}
          shareId={data?.row?.id}
          clipsCallback={(clipIds, linkedAssetId) => {
            updateClipsImportedStatus(data?.row?.id, clipIds, linkedAssetId);
          }}
        />
      ),
    },
    {
      field: "recipientEmails",
      headerName: t("table:headerName.sharedTo"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
          {data.value.map((mail) => (
            <Chip label={mail} size="small" sx={{ mb: 0.5 }} key={mail} />
          ))}
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      flex: CellSize.actionsXsmall,
      minWidth: CellSize.actionsXsmall,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        let array: any[] = [
          <GridActionsCellItem
            icon={<VisibilityIcon sx={{ width: 20, height: 20 }} />}
            label={t("table:cellLabel.visitLink")}
            onClick={() => handleShow(params.row)}
            showInMenu
            disabled={
              params?.row?.linkedOrderItemStatus === OrderStatus.READY ||
              params?.row?.linkedOrderItemStatus === OrderStatus.QUEUED ||
              params?.row?.linkedOrderItemStatus === OrderStatus.RUNNING
            }
          />,
          <GridActionsCellItem
            icon={<EditIcon sx={{ width: 20, height: 20 }} />}
            label={t("common:editUpperCase")}
            onClick={() => handleEditShare(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon sx={{ width: 20, height: 20 }} />}
            label={t("common:delete")}
            onClick={() => handleDelete(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<MoreHorizIcon sx={{ width: 20, height: 20 }} />}
            label={t("common:moreInfo")}
            onClick={() => openPanelForDetails(params.row)}
            showInMenu
          />,
        ];

        if (UserHasAcl(AclTypes.aclSupportTicketCreate)) {
          let platformSettings = cacheService.GetPlatformSettings();
          array.push(
            <TableCellActionReportIssue
              text={`
                ${t("common:platform")}: ${platformSettings?.name}\n
                ${t("table:cellText.shareId")}: ${params?.row?.id}\n
                ${t("field:label.shareName")}: ${params?.row?.name}\n
                ${t("table:cellText.objectId")}: ${params?.row?.objectId}\n
                ${t("table:cellText.titleId")}: ${params?.row?.titleId}
              `}
              typology="Share"
              objectId={params?.row?.id}
              showInMenu
              {...params}
            />
          );
        }

        return array;
      },
    },
  ];

  const openPanelForDetails = (share) => {
    setSelectedShare(share);
    if (!shareDetailsPanelOpen) toggleShareDetailsPanelOpen();
  };

  const openPanelForRowDetails = (e: GridRowParams) => {
    setSelectedShare(e.row as DbShareMediaInfos);
    if (!shareDetailsPanelOpen) toggleShareDetailsPanelOpen();
  };

  const handleShow = (share) => {
    OpenLinkInNewTab(routePaths.viewLink.path + "/" + share?.key);
  };

  const onSubmitEdit = (share: DbShareMediaInfos) => {
    setShares(
      produce((draft) => {
        let index = draft.findIndex((x) => x.id === share.id);

        if (index !== -1) draft[index] = share;
      })
    );
  };

  const handleEditShare = (share) => {
    if (shareDetailsPanelOpen) toggleShareDetailsPanelOpen();
    if (!openModal) return;

    openModal<ModalShareUpdateElementProps>(ModalShareUpdate, {
      share: share,
      onUpdated: onSubmitEdit,
    });
  };

  const handleMetricsShare = (share) => {
    if (shareDetailsPanelOpen) toggleShareDetailsPanelOpen();
    if (!openModal) return;

    openModal<ModalShareMetricsElementProps>(ModalShareMetrics, {
      share: share,
    });
  };

  const handleDelete = (share: DbShareMediaInfos) => {
    if (shareDetailsPanelOpen) toggleShareDetailsPanelOpen();

    setSelectedShare(share);
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      commentLabel: t("modal:comment.confirmShareDeletion"),
      title: t("modal:title.confirmShareDeletion"),
      onConfirm: () => handleDeleteConfirmed(share),
    });
  };

  const handleDeleteConfirmed = (share: DbShareMediaInfos) => {
    shareService.DeleteShareById(share.id).then(() => {
      customSnackbar(t("alert:success.shareDeleted"), "success");
      setShares((current) =>
        current.filter((c) => {
          return c.id !== share.id;
        })
      );

      if (share.linkedShareIds)
        setShares((current) =>
          current.filter((c) => {
            return !share.linkedShareIds?.includes(c.id);
          })
        );
    });
  };

  const openNewShareModal = () => {
    if (!openModal) return;
    openModal(ModalCreateShare);
  };

  // Filter
  //   const handleFilterType = (event: SelectChangeEvent<string>) => {
  //     if (event.target.value === undefined) setFilterType(undefined);
  //     else setFilterType(event.target.value as ShareMediaType);
  //   };
  const handleFilterStatus = (event: SelectChangeEvent<string>) => {
    setFilterStatus(event.target.value as string);
  };
  const handleFilterViewed = (event: SelectChangeEvent<string>) => {
    setFilterViewed(event.target.value as "" | "yes" | "never");
  };
  const handleFilterClips = (event: SelectChangeEvent<string>) => {
    setFilterClips(event.target.value as "" | "with" | "without");
  };
  const handleFilterNex = (event: SelectChangeEvent<string>) => {
    setFilterNex(event.target.value as "" | "with" | "without");
  };
  //   const handleFilterObject = (event: SelectChangeEvent<string>) => {
  //     setFilterObject(event.target.value as string);
  //   };

  const updateSearchValue = React.useCallback((newSearchValue: string) => {
    setFilterText(newSearchValue);
  }, []);

  const debouncedUpdateSearchValue = React.useMemo(() => debounce(500, updateSearchValue), [updateSearchValue]);

  const handleSearchValueChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchValue = event.target.value;
      debouncedUpdateSearchValue(newSearchValue);
    },
    [debouncedUpdateSearchValue]
  );

  const additionalFilters = (
    <>
      {/* <Select
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<MenuIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterType}
        onChange={(value) => handleFilterType(value)}
        disabled={isLoading}
        displayEmpty
      >
        <MenuItem value={undefined}>{t("common:type")}</MenuItem>
        <MenuItem value={ShareMediaType.StreamingBasic} key="type-basic">
          {t("common:basic")}
        </MenuItem>
        <MenuItem value={ShareMediaType.StreamingTagSecure} key="type-tag">
          {t("menu:item.securedStreaming")}
        </MenuItem>
      </Select> */}

      {UserHasLicence(Licences.WatermarkNexguardCivolution) && (
        <Select
          IconComponent={ExpandMoreRoundedIcon}
          startAdornment={<SecurityIcon />}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          value={filterNex}
          onChange={(value) => handleFilterNex(value)}
          disabled={isLoading}
          displayEmpty
        >
          <MenuItem value={""}>{t("menu:item.nexguard")}</MenuItem>
          <MenuItem value={"with"} key={"nxyes"}>
            {t("common:with")}
          </MenuItem>
          <MenuItem value={"without"} key={"nxno"}>
            {t("common:without")}
          </MenuItem>
        </Select>
      )}

      {/* <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<LightbulbOutlinedIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterObject}
        onChange={(value) => handleFilterObject(value)}
        disabled={isLoading}
      >
        <MenuItem value={""}>{t("common:object")}</MenuItem>
        {objects.map((type, index) => (
          <MenuItem value={type} key={index}>
            {type}
          </MenuItem>
        ))}
      </Select> */}

      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<EqualizerIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterStatus}
        onChange={(value) => handleFilterStatus(value)}
        disabled={isLoading}
      >
        <MenuItem value={""}>{t("common:status")}</MenuItem>
        {statuses.map((status, index) => (
          <MenuItem value={status} key={index}>
            {status}
          </MenuItem>
        ))}
      </Select>

      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<VisibilityIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterViewed}
        onChange={(value) => handleFilterViewed(value)}
        disabled={isLoading}
      >
        <MenuItem value={""}>{t("common:viewed")}</MenuItem>
        <MenuItem value={"yes"} key={"viewyes"}>
          {t("common:yes")}
        </MenuItem>
        <MenuItem value={"never"} key={"viewno"}>
          {t("common:never")}
        </MenuItem>
      </Select>

      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<ContentCutIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterClips}
        onChange={(value) => handleFilterClips(value)}
        disabled={isLoading}
      >
        <MenuItem value={""}>{t("common:clips")}</MenuItem>
        <MenuItem value={"with"} key={"clipyes"}>
          {t("common:with")}
        </MenuItem>
        <MenuItem value={"without"} key={"clipno"}>
          {t("common:without")}
        </MenuItem>
      </Select>
    </>
  );

  return (
    <>
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.SHARES_TABLE}
        rows={shares.filter(
          (x) =>
            (!filterType || x.type === filterType) &&
            (filterObject === "" || x.objectLayer === filterObject) &&
            (filterViewed === "" ||
              (filterViewed === "yes" && x.isViewed) ||
              (filterViewed === "never" && !x.isViewed)) &&
            (filterClips === "" ||
              (filterClips === "with" && x.clips && x.clips.length > 0) ||
              (filterClips === "without" && (!x.clips || x.clips.length === 0))) &&
            (filterNex === "" ||
              (filterNex === "with" && x.watermarkSecurity) ||
              (filterNex === "without" && !x.watermarkSecurity)) &&
            (filterStatus === "" ||
              (x.isActive === true &&
                (!x.linkedOrderItemStatus || x.linkedOrderItemStatus === OrderStatus.COMPLETED) &&
                filterStatus === "Active") ||
              (x.isActive === false && filterStatus === "Expired") ||
              ((x.linkedOrderItemStatus === OrderStatus.READY ||
                x.linkedOrderItemStatus === OrderStatus.QUEUED ||
                x.linkedOrderItemStatus === OrderStatus.RUNNING) &&
                filterStatus === "Transcoding"))
        )}
        columns={columns}
        disableRowSelectionOnClick
        onRowClick={openPanelForRowDetails}
        getRowHeight={() => "auto"}
        additionalFilters={additionalFilters}
        additionalActions={
          <Button
            startIcon={<AddIcon />}
            color="primary"
            variant="contained"
            onClick={openNewShareModal}
            disabled={!UserHasAcl(AclTypes.aclShareCreate)}
          >
            {t("button:newShare")}
          </Button>
        }
        initialColumnVisibility={{
          objectLayer: false,
          comment: false,
          isMultiple: false,
          isViewed: false,
          clips: false,
        }}
        apiRef={apiGrid}
        checkboxSelection
        selectedRows={shares.filter((x) => selectedShares.map((s) => s.id).includes(x.id)).map((s) => s.id)}
        setSelectedRows={(v) => setSelectedShares(shares.filter((x) => (v as number[]).includes(x.id)))}
      />

      <ShareDetailsPanel
        share={selectedShare}
        open={shareDetailsPanelOpen}
        onClose={toggleShareDetailsPanelOpen}
        openEditModal={() => handleEditShare(selectedShare)}
        openMetricsModal={() => handleMetricsShare(selectedShare)}
        deleteShare={() => handleDelete(selectedShare!)}
      />
    </>
  );
}

export default SharesList;
